/**
 * Operator Overview — the back-office control room (matches the product
 * prototype): a system-posture strip, headline KPIs, an active-incidents
 * ledger, a provider-health grid, a cost-anomaly callout and a high-risk audit
 * feed.
 *
 * HONESTY (spec rule #11): none of these telemetry surfaces has an operator
 * endpoint yet (system posture, incidents, provider health, costs, cross-tenant
 * audit are the §7 follow-ups). So the figures below come from a clearly
 * LABELLED sample fixture and the page carries a persistent "preview" banner.
 * The moment the operator endpoints ship, swap `SAMPLE` for the real hooks and
 * drop the banner — the layout does not change.
 */

import { useT } from '@hostesa/i18n';
import { Card, cn, MetricCard, StatusPill } from '@hostesa/ui';
import { ShieldAlert } from 'lucide-react';

const EYEBROW =
  'font-[var(--font-mono)] text-[11px] uppercase tracking-wider text-[var(--color-stone-mid)]';
const LABEL =
  'font-[var(--font-mono)] text-[11px] uppercase tracking-wider text-[var(--color-stone-mid)]';
const VALUE = 'font-[var(--font-display)] text-lg text-[var(--color-charcoal)]';
const META = 'font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]';
const SECTION =
  'flex items-center gap-3 font-[var(--font-mono)] text-[11px] uppercase tracking-wider text-[var(--color-stone-mid)]';

/** Clearly-labelled SAMPLE telemetry — replace with operator endpoints when they exist. */
const SAMPLE = {
  env: { environment: 'production', deploy: 'v4.21.0', region: 'eu-central' },
  posture: [
    { id: 'gateway', label: 'API gateway', value: 'ready', sub: 'readiness 200', dot: 'success' },
    { id: 'realtime', label: 'Realtime', value: 'connected', sub: '1,204 sockets', dot: 'success' },
    { id: 'queues', label: 'Queues', value: '47 deep', sub: 'oldest 8.2s', dot: 'alert' },
    { id: 'webhooks', label: 'Webhook errors', value: '1.4%', sub: 'Mews 502s', dot: 'alert' },
    { id: 'calls', label: 'Calls live', value: '17', sub: '3 in handoff', dot: 'neutral' },
    { id: 'cost', label: 'Cost today', value: '€1,220', sub: '+3% vs avg', dot: 'success' },
  ],
  kpis: [
    {
      id: 'incidents',
      label: 'Active incidents',
      value: '6',
      delta: '2 High · 2 Med',
      tone: 'down',
    },
    { id: 'calls', label: 'Calls in progress', value: '17', delta: 'live', tone: 'neutral' },
    { id: 'handoff', label: 'Handoff requests', value: '3', delta: '2 waiting', tone: 'down' },
    { id: 'failed', label: 'Failed calls 24h', value: '34', delta: '0.8%', tone: 'neutral' },
  ],
  incidents: [
    {
      id: 'INC-204',
      title: 'Elevated STT latency — eu-central',
      meta: 'on-call · Open',
      age: '12m',
      sev: 'High',
    },
    {
      id: 'INC-203',
      title: 'Belmar Burgas line 6 not registering',
      meta: 'Petar K. · Open',
      age: '48m',
      sev: 'Med',
    },
    {
      id: 'INC-202',
      title: 'Sunny Coast Tower A — 1 line degraded',
      meta: 'on-call · Investigating',
      age: '1h 20m',
      sev: 'Med',
    },
    {
      id: 'INC-201',
      title: 'Mews webhook retries elevated',
      meta: 'Ivan D. · Investigating',
      age: '2h',
      sev: 'Low',
    },
    {
      id: 'INC-198',
      title: 'Pirin Alpine both lines offline',
      meta: 'Petar K. · Open',
      age: '3h',
      sev: 'High',
    },
    {
      id: 'INC-195',
      title: 'Knowledge crawl warnings — Bansko',
      meta: '— · Monitoring',
      age: '5h',
      sev: 'Low',
    },
  ],
  providers: [
    { id: 'vapi', label: 'Vapi', value: '310ms', dot: 'success' },
    { id: 'livekit', label: 'LiveKit', value: '88ms', dot: 'success' },
    { id: 'deepgram', label: 'Deepgram', value: '410ms ↑', dot: 'alert' },
    { id: 'openai', label: 'OpenAI', value: '620ms', dot: 'neutral' },
    { id: 'messaging', label: 'Messaging', value: 'OK', dot: 'success' },
    { id: 'pms', label: 'PMS · Mews', value: 'retries ↑', dot: 'alert' },
  ],
  audit: [
    {
      id: 'a1',
      time: '21:09:55',
      action: 'pii.phone.reveal',
      actor: 'Ivan Dimitrov · C-8809',
      scope: 'Sofia City Hotels',
      outcome: 'success',
    },
    {
      id: 'a2',
      time: '20:54:12',
      action: 'admin.impersonate.started',
      actor: 'Nadia Koleva · HOS-4830',
      scope: 'Vila Rila Group',
      outcome: 'success',
    },
    {
      id: 'a3',
      time: '20:31:07',
      action: 'integration.credentials.rotated',
      actor: 'Petar K. · C-8801',
      scope: 'Sunny Coast Group',
      outcome: 'success',
    },
  ],
} as const;

const DOT: Record<string, string> = {
  success: 'bg-[var(--color-success)]',
  alert: 'bg-[var(--color-alert)]',
  neutral: 'bg-[var(--color-stone-400)]',
};
const SEV_TONE: Record<string, 'alert' | 'accent' | 'neutral'> = {
  High: 'alert',
  Med: 'accent',
  Low: 'neutral',
};

export function OverviewPage() {
  const { t } = useT('backoffice');
  const s = SAMPLE;

  return (
    <section
      data-testid="overview-page"
      className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-8 py-8"
    >
      {/* Header: title + env meta + posture pill + honest preview badge */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className={EYEBROW}>{t('overview.eyebrow')}</span>
          <h1 className="font-[var(--font-display)] text-2xl text-[var(--color-charcoal)]">
            {t('overview.title')}
          </h1>
        </div>
        <div className="flex items-center gap-6">
          {(['environment', 'deploy', 'region'] as const).map((k) => (
            <div key={k} className="flex flex-col">
              <span className={LABEL}>{t(`overview.env.${k}`)}</span>
              <span className="font-[var(--font-mono)] text-sm text-[var(--color-charcoal)]">
                {s.env[k]}
              </span>
            </div>
          ))}
          <StatusPill
            data-testid="overview-status-pill"
            tone="alert"
            label={t('overview.degraded')}
          />
        </div>
      </header>

      {/* Honest preview notice — telemetry is sample data until operator endpoints ship. */}
      <div
        data-testid="overview-preview-notice"
        role="note"
        className="flex items-start gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-warn-wash)] px-4 py-3"
      >
        <ShieldAlert className="mt-0.5 size-4 shrink-0 text-[var(--color-alert)]" />
        <p className="text-sm text-[var(--color-charcoal)]">
          <strong className="font-[var(--font-display)]">{t('overview.preview.label')}</strong>{' '}
          <span className="text-[var(--color-stone-mid)]">{t('overview.preview.body')}</span>
        </p>
      </div>

      {/* System posture strip */}
      <div className={SECTION}>
        <span className="h-px w-6 bg-[var(--color-border)]" />
        {t('overview.sections.posture')}
      </div>
      <Card className="grid grid-cols-2 gap-px overflow-hidden bg-[var(--color-border)] p-0 sm:grid-cols-3 lg:grid-cols-6">
        {s.posture.map((p) => (
          <div key={p.id} className="flex flex-col gap-1 bg-[var(--color-surface)] p-4">
            <span className={LABEL}>{p.label}</span>
            <span className="flex items-center gap-2">
              <span className={cn('size-1.5 rounded-full', DOT[p.dot])} />
              <span className={VALUE}>{p.value}</span>
            </span>
            <span className={META}>{p.sub}</span>
          </div>
        ))}
      </Card>

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {s.kpis.map((k) => (
          <MetricCard
            key={k.id}
            data-testid={`overview-kpi-${k.id}`}
            label={k.label}
            value={k.value}
            delta={{ value: k.delta, tone: k.tone as 'down' | 'neutral' }}
          />
        ))}
      </div>

      {/* Incidents (left) + provider health & cost anomaly (right) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <Card data-testid="overview-incidents" className="flex flex-col p-0">
          <div className="flex items-center justify-between px-5 py-3.5">
            <span className={SECTION}>{t('overview.sections.incidents')}</span>
            <span className={META}>{t('overview.allIncidents')} →</span>
          </div>
          <ul className="flex flex-col">
            {s.incidents.map((inc) => (
              <li
                key={inc.id}
                className="flex items-center justify-between gap-3 border-t border-[var(--color-border)] px-5 py-3"
              >
                <div className="flex min-w-0 items-start gap-3">
                  <span
                    className={cn(
                      'mt-1 h-8 w-0.5 shrink-0 rounded-full',
                      inc.sev === 'High' ? 'bg-[var(--color-alert)]' : 'bg-[var(--color-border)]',
                    )}
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm text-[var(--color-charcoal)]">{inc.title}</p>
                    <p className={META}>{`${inc.id} · ${inc.meta}`}</p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className={META}>{inc.age}</span>
                  <StatusPill tone={SEV_TONE[inc.sev]} label={inc.sev} />
                </div>
              </li>
            ))}
          </ul>
        </Card>

        <div className="flex flex-col gap-6">
          <Card data-testid="overview-providers" className="flex flex-col gap-3 p-5">
            <span className={SECTION}>{t('overview.sections.providerHealth')}</span>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {s.providers.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between rounded-md border border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2"
                >
                  <span className="flex items-center gap-2 text-sm text-[var(--color-charcoal)]">
                    <span className={cn('size-1.5 rounded-full', DOT[p.dot])} />
                    {p.label}
                  </span>
                  <span className={META}>{p.value}</span>
                </div>
              ))}
            </div>
          </Card>

          <div
            data-testid="overview-cost-anomaly"
            className="flex items-start gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-warn-wash)] px-4 py-3"
          >
            <span className="font-[var(--font-mono)] text-sm text-[var(--color-alert)]">$</span>
            <p className="text-sm text-[var(--color-charcoal)]">
              {t('overview.costAnomaly')}{' '}
              <span className="text-[var(--color-stone-mid)]">· {t('overview.inspect')} →</span>
            </p>
          </div>
        </div>
      </div>

      {/* Recent high-risk audit events */}
      <Card data-testid="overview-audit" className="flex flex-col p-0">
        <div className="flex items-center justify-between px-5 py-3.5">
          <span className={SECTION}>{t('overview.sections.audit')}</span>
          <span className={META}>{t('overview.fullAuditLog')} →</span>
        </div>
        <ul className="flex flex-col">
          {s.audit.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between gap-3 border-t border-[var(--color-border)] px-5 py-3"
            >
              <div className="flex min-w-0 items-center gap-4">
                <span className={cn(META, 'shrink-0')}>{a.time}</span>
                <div className="min-w-0">
                  <p className="truncate font-[var(--font-mono)] text-sm text-[var(--color-charcoal)]">
                    {a.action}
                  </p>
                  <p className={META}>{a.actor}</p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className={META}>{a.scope}</span>
                <StatusPill tone="success" label={a.outcome} />
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}
